import type { SvgIconComponent } from '@mui/icons-material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TerminalIcon from '@mui/icons-material/Terminal';
import { Box, Chip, Container, Grid, Typography } from '@mui/material';
import React from 'react';
import { PageTemplate } from '~/components/PageTemplate';
import { McpInstructionsCard } from '~/scenes/api/McpInstructionsCard';
import { FakeTerminal } from '~/scenes/mcp/FakeTerminal';
import { getPageMetaTags } from '~/scripts/getPageMetaTags';
import type { Route } from './+types/mcp';
import { ResearchToolsButtons } from '~/components/ResearchToolsButtons';
import { ChatGptPluginCta } from '~/components/ChatGptPluginCta';
import { ApiFreeTierCard } from '~/scenes/api/ApiFreeTierCard';

export const meta: Route.MetaFunction = () => {
  return getPageMetaTags({
    title:
      'NerdyData MCP Server - Use NerdyData in Claude, Cursor, ChatGPT & more',
    description:
      'Search millions of websites by technology, look up domain profiles, and export lead lists through Claude Desktop, Cursor, Windsurf, and any MCP-compatible AI assistant.',
    canonical: 'https://www.nerdydata.com/mcp',
    faq: [
      {
        question: 'What is the NerdyData MCP Server?',
        answer:
          'The NerdyData MCP (Model Context Protocol) Server is a plugin that connects AI assistants like Claude Desktop, Cursor, Windsurf, and ChatGPT to NerdyData. It lets you search websites by technology, look up domain profiles, and export lead lists directly inside your AI workflow. For example, you can ask your LLM: "How many websites use Shopify?" or "Which websites reference `cdn.shopify.com` in their source code?"',
      },
      {
        question: 'Which AI clients support the NerdyData MCP Server?',
        answer:
          'The NerdyData MCP Server works with any MCP-compatible client that supports local (stdio) servers, including Claude Desktop, Claude Code, Cursor, Windsurf, Gemini CLI, and VS Code with GitHub Copilot.',
      },
      {
        question: 'What can I do with the NerdyData MCP Server?',
        answer:
          'You can search millions of websites by technology or code snippet, look up domain profiles including company name and spend estimates, and export lists of matching websites — all from within your AI assistant.',
      },
      {
        question: 'How do I install the NerdyData MCP Server?',
        answer:
          'Installation instructions are available on the NerdyData MCP page at nerdydata.com/mcp. You will need a NerdyData API key to authenticate.',
      },
    ],
  });
};

export const loader = async ({ context: _context }: Route.LoaderArgs) => {
  return null;
};

const supportedClients: { name: string; Icon: SvgIconComponent }[] = [
  { name: 'Claude Desktop', Icon: SmartToyIcon },
  { name: 'Claude Code', Icon: TerminalIcon },
  { name: 'Cursor', Icon: SmartToyIcon },
  { name: 'Windsurf', Icon: SmartToyIcon },
  { name: 'Gemini CLI', Icon: TerminalIcon },
  { name: 'VS Code', Icon: SmartToyIcon },
  { name: 'Any MCP Client', Icon: TerminalIcon },
];

const tools: { name: string; description: React.ReactNode }[] = [
  {
    name: 'get_suggestions',
    description:
      'Find technology report IDs by name (e.g. "Shopify", "Klaviyo").',
  },
  {
    name: 'get_suggestions_categories',
    description:
      'Browse technology categories to discover what types of technologies can be searched.',
  },
  {
    name: 'search_code',
    description:
      'Search websites by technology or code snippet. Returns company info, emails, LinkedIn, spend estimates.',
  },
  {
    name: 'create_download',
    description: 'Queue a full CSV export of matching websites.',
  },
  {
    name: 'get_download',
    description: 'Poll a download job by ID. Returns a file URL when ready.',
  },
  {
    name: 'get_domain',
    description:
      "Look up a domain's metadata: company, employees, spend, emails, social profiles.",
  },
  {
    name: 'get_domain_technologies',
    description: 'See what technologies a specific domain uses.',
  },
];

const exampleQueries: { heading: string; queries: string[] }[] = [
  {
    heading: 'Find leads',
    queries: [
      'Find websites that use Shopify',
      'How many websites use Klaviyo?',
      'Find e-commerce sites using WooCommerce in the US',
      'Which websites have Intercom installed?',
    ],
  },
  {
    heading: 'Research competitors',
    queries: [
      'What technologies does shopify.com use?',
      'Does example.com use any marketing automation tools?',
      'Compare the tech stacks of stripe.com and braintreepayments.com',
    ],
  },
  {
    heading: 'Export lists',
    queries: [
      'Export a CSV of all websites using HubSpot',
      'Download a list of sites running WordPress with WooCommerce',
    ],
  },
  {
    heading: 'Explore by category',
    queries: [
      'What technology categories can I search?',
      'Show me CRM tools I can search for',
      'Find websites using any live chat technology',
    ],
  },
];

const troubleshootingItems: { question: string; answer: string }[] = [
  {
    question: 'The server is not connecting',
    answer:
      'Make sure Node.js is installed and that npx can run packages. Try running `npx -y @nerdydata/mcp` in your terminal to confirm.',
  },
  {
    question: 'I get an authentication error',
    answer:
      'Check that your NERDYDATA_API_KEY is set correctly in the config and matches the key shown in your NerdyData account settings.',
  },
  {
    question: 'The AI assistant cannot find a technology',
    answer:
      'Use the get_suggestions tool first to look up the correct report ID for the technology you want to search.',
  },
  {
    question: 'My download never completes',
    answer:
      'Large exports can take a few minutes. Use get_download with the job ID returned by create_download and poll until the status is ready.',
  },
];

export default function McpPage({}: Route.ComponentProps) {
  return (
    <PageTemplate
      breadcrumbs={[
        { href: '/', text: 'Home' },
        { href: '/integrations', text: 'Integrations' },
        { href: '/mcp', text: 'MCP Server', disabled: true },
      ]}
      centered
      title="NerdyData MCP Server"
      subtitle="Search websites by technology through your AI assistant."
      description="Ask Claude, Cursor, or ChatGPT to find leads, look up domain tech stacks, and export lists - all in natural language.">
      {/* ChatGPT plugin */}
      <Grid container justifyContent="center" sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <ChatGptPluginCta />
        </Grid>
      </Grid>

      {/* Terminal demo */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12}>
          <FakeTerminal />
        </Grid>
      </Grid>

      {/* Supported clients */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} textAlign="center">
          <Typography
            variant="overline"
            color="text.secondary"
            display="block"
            gutterBottom>
            Works with
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1.5,
            }}>
            {supportedClients.map(({ name, Icon }) => {
              return (
                <Chip
                  key={name}
                  label={name}
                  variant="outlined"
                  icon={<Icon fontSize="small" />}
                />
              );
            })}
          </Box>
        </Grid>
      </Grid>

      <Container sx={{ pt: 4, mb: 6 }}>
        <ApiFreeTierCard />
      </Container>

      {/* Typical workflow */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Typography variant="h5" gutterBottom>
            How it works
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}>
            {[
              {
                step: '1',
                label: 'Discover',
                detail: 'Browse technology categories or search by name',
              },
              {
                step: '2',
                label: 'Search',
                detail: 'Find websites using that technology',
              },
              {
                step: '3',
                label: 'Profile',
                detail: 'Look up company details and tech stacks',
              },
              {
                step: '4',
                label: 'Export',
                detail: 'Download a CSV of matching leads',
              },
            ].map(({ step, label, detail }) => {
              return (
                <Box
                  key={step}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}>
                  <Typography variant="overline" color="text.secondary">
                    Step {step}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detail}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      {/* Setup instructions */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <McpInstructionsCard />
        </Grid>
      </Grid>

      {/* Available tools */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Typography variant="h5" gutterBottom>
            Available tools
          </Typography>
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="tbody">
              {tools.map((tool) => {
                return (
                  <Box
                    component="tr"
                    key={tool.name}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}>
                    <Box
                      component="td"
                      sx={{
                        py: 1.5,
                        pr: 3,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'top',
                      }}>
                      {tool.name}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        py: 1.5,
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }}>
                      {tool.description}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>
      {/* Example queries */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Typography variant="h5" gutterBottom>
            Example queries
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Once connected, try asking your AI assistant:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {exampleQueries.map(({ heading, queries }) => {
              return (
                <Box key={heading}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {heading}
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}>
                    {queries.map((query) => {
                      return (
                        <Box component="li" key={query}>
                          <Typography variant="body2" color="text.secondary">
                            &ldquo;{query}&rdquo;
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      {/* Troubleshooting */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Typography variant="h5" gutterBottom>
            Troubleshooting
          </Typography>
          <Box>
            {troubleshootingItems.map(({ question, answer }) => {
              return (
                <Box
                  key={question}
                  sx={{
                    py: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    component="h3"
                    gutterBottom>
                    {question}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {answer}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      {/* FAQ */}
      <Grid container justifyContent="center" sx={{ mb: 6 }}>
        <Grid item xs={12} md={10} lg={8}>
          <Typography variant="h5" gutterBottom>
            Frequently asked questions
          </Typography>
          <Box>
            {[
              {
                question: 'What is the NerdyData MCP Server?',
                answer:
                  'The NerdyData MCP (Model Context Protocol) Server is a plugin that connects AI assistants like Claude Desktop, Cursor, Windsurf, and ChatGPT to NerdyData. It lets you search websites by technology, look up domain profiles, and export lead lists directly inside your AI workflow.',
              },
              {
                question: 'Which AI clients support the NerdyData MCP Server?',
                answer:
                  'The NerdyData MCP Server works with any MCP-compatible client that supports local (stdio) servers, including Claude Desktop, Claude Code, Cursor, Windsurf, Gemini CLI, and VS Code with GitHub Copilot.',
              },
              {
                question: 'What can I do with the NerdyData MCP Server?',
                answer:
                  'You can search millions of websites by technology or code snippet, look up domain profiles including company name and spend estimates, and export lists of matching websites — all from within your AI assistant.',
              },
              {
                question: 'How do I install the NerdyData MCP Server?',
                answer:
                  'Installation instructions are available on this page. You will need a NerdyData API key to authenticate.',
              },
            ].map(({ question, answer }) => {
              return (
                <Box
                  key={question}
                  sx={{
                    py: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    component="h3"
                    gutterBottom>
                    {question}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {answer}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <ResearchToolsButtons extension api compare />
        </Grid>
      </Grid>
    </PageTemplate>
  );
}
